import * as React from 'react'

import { useLocation, useHistory } from 'react-router-dom'
import { OneBlinkForm } from '@oneblink/apps-react'
import { formService, submissionService } from '@oneblink/apps-react'
import { FormTypes } from '@oneblink/types'
import { v4 as uuidv4 } from 'uuid'
import _cloneDeep from 'lodash.clonedeep'

import { LeftAlignedPageContent } from '../../components/LayoutComponents'
import useFetchForm from '../../hooks/useFetchForm'
import config from '../../config'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import { postRequest } from '../../lib/http'
import useQuery from '../../hooks/useQuery'
import useSanitisedParams from '../../hooks/useSanitisedParams'

import { useAppConfig } from '../../providers/AppConfigProvider'
import useGoogleAnalyticsFormBeforeUnload from '../../hooks/useGoogleAnalyticsFormBeforeUnload'

function elementAlreadyAppended(elements: FormTypes.FormElement[]) {
  return elements.find(
    (el) =>
      el.type !== 'page' &&
      el.type !== 'section' &&
      ['loading', 'confirmEmailed__info', 'emailError'].includes(el.name),
  )
}

export default function DraftReceipt() {
  const { trackingCode } = useSanitisedParams<{ trackingCode: string }>()
  const { formId: qsFormId } = useQuery()
  const { buttons } = useAppConfig()
  const [initialSubmission, setInitialSubmission] = React.useState<
    Record<string, unknown>
  >({})

  const [fetchDraftMetaError, setFetchDraftMetaError] =
    React.useState<boolean>(false)
  const [isFetching, setIsFetching] = React.useState(true)

  const { formDefinition, isFetchingForm, fetchError } = useFetchForm(
    config.FORMS.SAVE_DRAFT_RECEIPT_FORM_ID,
    undefined,
    false,
  )

  const savedFormId = qsFormId ? parseInt(qsFormId.toString()) : NaN

  const [key, setKey] = React.useState(0)

  const [draftReceiptFormDefinition, setDraftReceiptFormDefinition] =
    React.useState<FormTypes.Form | null>(null)
  const [cancelError, setCancelError] = React.useState(false)

  const location = useLocation<{ email: string } | null>()
  const history = useHistory()

  const customButtons = React.useMemo(() => {
    return {
      ...buttons,
      submit: { label: 'Send', icon: 'mail_outline' },
      cancel: { label: 'Close' },
    }
  }, [buttons])

  const onBeforeUnload = useGoogleAnalyticsFormBeforeUnload({
    form: draftReceiptFormDefinition ?? formDefinition ?? undefined,
    isSubmitting: false,
  })

  const onSubmit = React.useCallback(
    async (newFormSubmission: submissionService.NewFormSubmission) => {
      const { submission } = newFormSubmission
      setInitialSubmission(submission)
      let _formDefinition

      if (draftReceiptFormDefinition) {
        _formDefinition = _cloneDeep(draftReceiptFormDefinition)
      }

      // we'll always have this, just making TS happy
      if (!_formDefinition || !_formDefinition.elements) {
        return
      }

      const loadingElement: FormTypes.FormElement = {
        id: uuidv4(),
        type: 'html',
        name: 'loading',
        label: 'loading',
        defaultValue: '<div class="on-loading small"></div>',
        conditionallyShow: false,
        requiresAllConditionallyShowPredicates: false,
      }
      if (elementAlreadyAppended(_formDefinition.elements)) {
        _formDefinition.elements[_formDefinition.elements.length - 1] =
          loadingElement
      } else {
        _formDefinition.elements.push(loadingElement)
      }
      setDraftReceiptFormDefinition(_formDefinition)
      setKey((currentValue) => currentValue + 1)

      const payload = {
        email: submission.email_address_share,
        trackingCode: trackingCode.substring(0, 6).toUpperCase(),
        customMessage: submission.message,
        formsAppId: config.FORMS_APP_ID,
        formId: savedFormId,
      }

      // default to the sad path
      let newElement: FormTypes.FormElement = {
        id: uuidv4(),
        type: 'html',
        name: 'emailError',
        label: 'emailError',
        defaultValue: `<div class='has-text-centered'>
                  <div>
                    <i class="material-icons has-text-danger">error_outline</i>
                    <p>There was an error sending draft reference code.</p>
                  </div>
                </div>`,
        conditionallyShow: false,
        requiresAllConditionallyShowPredicates: false,
      }
      try {
        await postRequest({
          url: `${config.HOSTED_API_URL}/email-draft-tracking-code`,
          resource: payload,
          addAuthHeaders: true,
        })
        newElement = {
          id: uuidv4(),
          type: 'html',
          name: 'confirmEmailed__info',
          label: 'confirmEmailed',
          defaultValue: `An email has been successfully sent to <b>${payload.email}</b> with instructions on how to resume the form at a later date.`,
          conditionallyShow: false,
          requiresAllConditionallyShowPredicates: false,
        }
      } catch (e) {
        console.log('error on submission')
        console.error(e)
      } finally {
        //if loading element exists (it should) then replace it, else append info
        if (elementAlreadyAppended(_formDefinition.elements)) {
          _formDefinition.elements[_formDefinition.elements.length - 1] =
            newElement
        } else {
          _formDefinition.elements.push(newElement)
        }
        setDraftReceiptFormDefinition(_formDefinition)
        setKey((currentValue) => currentValue + 1)
        window.scrollTo(0, document.body.scrollHeight)
      }
    },
    [draftReceiptFormDefinition, savedFormId, trackingCode],
  )

  const onCancel = React.useCallback(async () => {
    if (formDefinition) {
      try {
        await submissionService.executeCancelAction(
          { definition: formDefinition, externalId: null },
          {
            onRedirectToAbsoluteUrl: (url) => window.location.assign(url),
            onRedirectToRelativeUrl: history.push,
          },
        )
      } catch (e) {
        setCancelError(true)
      }
    }
  }, [formDefinition, history.push])

  React.useEffect(() => {
    const fetchData = async () => {
      if (!trackingCode || !formDefinition) {
        return
      }
      const _formDefinition = _cloneDeep(formDefinition)
      let email
      if (location && location.state && location.state.email) {
        email = location.state.email
        window.history.replaceState({}, window.document.title)
      }

      // inject elements
      if (savedFormId) {
        try {
          const associatedFormDefinition = await formService.getForm({
            formId: savedFormId,
            formsAppId: undefined,
            formSlug: undefined,
            formsAppEnvironmentId: formDefinition.formsAppEnvironmentId,
          })
          // if we can get the formName and make a heading element with it
          if (associatedFormDefinition && associatedFormDefinition.name) {
            const formNameElement: FormTypes.FormElement = {
              id: uuidv4(),
              conditionallyShow: false,
              requiresAllConditionallyShowPredicates: false,
              type: 'heading',
              name: 'formName',
              label: associatedFormDefinition.name,
              headingType: 2,
            }
            _formDefinition.elements.splice(0, 0, formNameElement)
          }
          // update the info element

          const trackingCodeReceiptElementIndex =
            _formDefinition.elements.findIndex(
              (el) => el.type === 'html' && el.name === 'trackingCode__receipt',
            )
          const html = `
            <div>
              <p><b>Your reference code is:</b></p>
              <span class="fa-info-panel__tracking-code">${trackingCode}</span>
              <p><a href="/resume-draft?trackingCode=${trackingCode}">Click here to return to your form</a>.</p>
            </div>`
          const trackingCodeReceiptElement =
            _formDefinition.elements[trackingCodeReceiptElementIndex]
          if (trackingCodeReceiptElement.type === 'html') {
            trackingCodeReceiptElement.defaultValue = html
            _formDefinition.elements[trackingCodeReceiptElementIndex] =
              trackingCodeReceiptElement
          }
        } catch (e) {
          setFetchDraftMetaError(true)
        }
      }
      setDraftReceiptFormDefinition(_formDefinition)
      setInitialSubmission({ email_address_share: email })
      setIsFetching(false)
    }

    fetchData()
  }, [
    setInitialSubmission,
    trackingCode,
    location,
    setDraftReceiptFormDefinition,
    formDefinition,
    savedFormId,
  ])

  if (isFetchingForm || isFetching) {
    return <LoadingSpinner>Fetching data.</LoadingSpinner>
  }

  if (!trackingCode) {
    return <ErrorMessage>Missing reference number.</ErrorMessage>
  }

  if (
    fetchError ||
    fetchDraftMetaError ||
    !draftReceiptFormDefinition ||
    !formDefinition
  ) {
    return (
      <ErrorMessage>
        An error has occurred while attempting to load this page.
      </ErrorMessage>
    )
  }

  if (cancelError) {
    return <ErrorMessage>Please close your browser tab.</ErrorMessage>
  }

  return (
    <React.Fragment key={key}>
      <LeftAlignedPageContent className="fa-draft-receipt">
        <OneBlinkForm
          audience="SUBMITTER"
          initialSubmission={initialSubmission}
          form={draftReceiptFormDefinition}
          onCancel={onCancel}
          onSubmit={onSubmit}
          onBeforeUnload={onBeforeUnload}
          buttons={customButtons}
          isPendingQueueEnabled={false}
        ></OneBlinkForm>
      </LeftAlignedPageContent>
    </React.Fragment>
  )
}
